/* eslint-disable no-use-before-define, eqeqeq, react-hooks/exhaustive-deps */
import React from 'react'
import { getState, arrayFrom, Status, isEmpty, numberFrom, parseDate, sortFn, publish, showModal } from '../twd/twd'
import usePositionsInfo, { resetLastPositionsInfo } from './use-positions-info'
import useOpenOrdersInfo, { resetLastOpenOrdersInfo } from './use-open-orders-info'
import useAccountInfo, { resetLastAccountInfo } from './use-account-info'
import useBondInfo, { resetLastBondInfo }  from './use-bond-info'
import useOrderInfo, { resetLastOrderInfo } from './use-order-info'
import useOrderForm, { resetLastOrderForm } from './use-order-form'
import useAttestations from './use-attestations'
import useValidation from './use-validation'
import useModals from './use-modals'
import { reset } from './use-order-ticket-reset'
import { preview } from './use-order-ticket-preview'
import { confirm } from './use-order-ticket-confirm'
import { AttestationsCD } from './form/attestations-cd'

export const Context = React.createContext()

const Last = getState('order-ticket.last', {
    ts: 0,
    snapshotView:'snapshot'
})

export const MODES = {
    RFQ: 'rfq', // to create rfq
    ORDER: 'order', // to create one B/S/W/F
    ORDER_Z: 'order-z', // to confirm order
    TRADE: 'trade', // from place-a-trade
}

export const STEPS = {
    ENTRY: 'entry',
    PREVIEW: 'preview',
    CONFIRM: 'confirm',
}

let resetingTicket = false

export const ContextProvider = ({
    action:action_ ='',
    acctNo:acctNo_ = getDefaultAcctNo(),
    cusip:cusip_ = '',
    qty:qty_ = '',
    price:price_ = '',
    yld:yld_ = '',
    executionType:executionType_ = '',
    sourceAction:sourceAction_ = '',
    sourceOrderNo:sourceOrderNo_ = '',
    actionSubset:actionSubset_ = '',
    ts:ts_,
    backFrom:backFrom_ = '',
    from:from_  =  '',
    step:step_ = '',
    isTreasuryAuction:isTreasuryAuction_ = false,
    settlement:settlement_ = '',
    children,
} = {}) => {

    let isReset = ts_ && Last.value.ts < ts_ && !from_
    if (isReset) {
        Last.reset()
        Last.set({ts:ts_, snapshotView:'snapshot', from:from_})
        resetLastBondInfo()
        resetLastOrderInfo()
        resetLastOrderForm()
        resetLastPositionsInfo()
        resetLastOpenOrdersInfo()
        resetLastAccountInfo()
        setTimeout(() => {
            setIsTreasuryAuction(isTreasuryAuction_)
            setSnapshotView('snapshot')
        })
    }


    const [ts, setTs_] = React.useState(ts_ || Last.value.ts || Date.now())
    const setTs = React.useCallback((ts) => {
        Last.set({ts})
        setTs_(ts)
    }, [])

    const [isTreasuryAuction, setIsTreasuryAuction_] = React.useState(isTreasuryAuction_)
    const setIsTreasuryAuction = React.useCallback((isTreasuryAuction) => {
        Last.set({isTreasuryAuction})
        setIsTreasuryAuction_(isTreasuryAuction)
    }, [])


    const [from, setFrom_] = React.useState(from_ || Last.value.from || '')
    const setFrom = React.useCallback((from) => {
        Last.set({from})
        setFrom_(from)
    }, [])

    const [step, setStep_] = React.useState(step_ || Last.value.step || STEPS.ENTRY)
    const setStep = React.useCallback((step) => {
        Last.set({step})
        setStep_(step)
    }, [])

    const [snapshotView, setSnapshotView_] = React.useState(Last.value.snapshotView || 'snapshot')
    const setSnapshotView = React.useCallback((snapshotView) => {
        Last.set({snapshotView})
        setSnapshotView_(snapshotView)
    }, [])

    const validation = useValidation()
    const validateTicket = (override={}) => {
        return validation.validate({
            ...orderForm,
            ...orderInfo,
            ...bondInfo,
            isChangeToRfq,
            isRestricted,
            isTreasuryAuction,
            ...override
        })
    }

    const orderForm = useOrderForm({
        ts_, acctNo_, action_, cusip_, qty_, price_, yld_, sourceAction_, actionSubset_,
        validateTicket,
    })

    const positionsInfo = usePositionsInfo({...orderForm, ts})
    const openOrdersInfo = useOpenOrdersInfo({...orderForm, ts})
    const accountInfo = useAccountInfo({...orderForm, ts})
    const bondInfo = useBondInfo({orderForm,  ts})
    const modals = useModals()

    const orderInfo = useOrderInfo({
        step,
        ...modals,
        ...orderForm,
        sourceAction:sourceAction_,
        sourceOrderNo:sourceOrderNo_,
        ts,
        from,
    })

    const attestations = useAttestations({...bondInfo, ...orderInfo})

    const resetTicket = ({
        acctNo='', cusip='', action='', qty='', price='', yld='',
        sourceAction_='', actionSubset_='', sourceOrderNo_='',
        ts=0,
    } = {}) => {
        resetingTicket = true
        Last.reset()
        setTs(ts)
        setIsChangeToRfq(false)
        setIsRestricted(false)
        setIsPreviewed(false)

        setStep(STEPS.ENTRY)
        acctNo = acctNo || getDefaultAcctNo()
        orderForm.reset({ts, cusip, action, qty, price, yld, acctNo, sourceAction_, actionSubset_,})
        accountInfo.reset()
        positionsInfo.reset()
        bondInfo.reset()
        orderInfo.reset()
        validation.reset()
        modals.reset()
        reset({
            ...orderForm,
            ...orderInfo,
            ...validation,
            cusip, action, qty, price, yld, acctNo,
            sourceAction_, actionSubset_, sourceOrderNo_,
            isTreasuryAuction,
            callback:(info) => {
                bondInfo.updateBondInfo({ cusip, ts, isReset:true })
                positionsInfo.updatePositions({acctNo, ts, isReset:true})

                if (!isTreasuryAuction
                    && orderForm.mode!==MODES.ORDER_Z
                    && !info.price
                    && ['S','B'].indexOf(action)>=0
                ) {
                    orderForm.setAction(action === 'S' ? 'W' : 'F')
                    orderForm.setNoMarketRfq(true)
                    return
                }

                if (['S','B'].indexOf(action)>=0) {
                    publish('order_ticket_recalc_totals')
                }
            }
        })

        setTimeout(() => {
            resetingTicket = false
        })
    }

    React.useEffect(() => {
        if (backFrom_) return

        if (action_==='S'
            && sourceAction_==='H'
            && !cusip_
        ) {
            acctNo_ = acctNo_ || getDefaultAcctNo()
            cusip_ = getFirstCusip(acctNo_)
        }

        resetTicket({
            ts:ts_ || Date.now(),
            acctNo:acctNo_,
            cusip:cusip_,
            action:action_,
            qty:qty_,
            price:price_,
            yld:yld_,
            sourceAction_,
            actionSubset_,
            sourceOrderNo_,
        })
        setFrom(from_)

    }, [ts_, acctNo_, cusip_, action_, qty_, price_, yld_, sourceAction_, actionSubset_, sourceOrderNo_, backFrom_, from_])

    const [ticketStatus, setTicketStatus] = React.useState(Status.LOADED)
    const [autoPreview, setAutoPreview] = React.useState(false)
    const [isPreviewed, setIsPreviewed] = React.useState(false)

    const [isChangeToRfq, setIsChangeToRfq_] = React.useState(Last.value.isChangeToRfq || false)
    const setIsChangeToRfq = React.useCallback((isChangeToRfq) => {
        Last.set({isChangeToRfq})
        setIsChangeToRfq_(isChangeToRfq)
    }, [])

    const [isRestricted, setIsRestricted_] = React.useState(Last.value.isRestricted || false)
    const setIsRestricted = React.useCallback((isRestricted) => {
        Last.set({isRestricted})
        setIsRestricted_(isRestricted)
    }, [])

    React.useEffect(() => {
        setIsRestricted(
            orderForm.mode!==MODES.ORDER_Z
            && bondInfo.hasBondInfo
            && bondInfo.isBondRestricted
            && ['S','W'].indexOf(orderForm.action)<0)
    }, [orderForm.mode, bondInfo.hasBondInfo, bondInfo.isBondRestricted, orderForm.action])


    const changeToRfq = () => {
        let { action, setAction,} = orderForm
        const a = action
        action = action === 'S' ? 'W' :
            action === 'B' ? 'F' :
            action
        if (a === action) return

        setAction(action)
        setAutoPreview(true)
        return true
    }

    const getIsChangeToRfq = ({orderInfo, orderForm, bondInfo}) => {

        if (['S','B'].indexOf(orderForm.action)<0) { return }

        if (orderInfo.invType==='M'
            && (orderInfo.statusMsg || '').indexOf('10329')>0
            && orderForm.qty<5
        ) {
            return
        }

        const { qty, max_qty_avail, qty_min, qty_incr } = orderInfo
        const hasQty = qty
            && qty <= max_qty_avail
            && qty >= qty_min
            && qty % qty_incr === 0

        return orderForm.mode!==MODES.ORDER_Z
            && bondInfo.hasBondInfo
            && (
                (!bondInfo.isBondRestricted && !orderInfo.hasMarket)
                || (bondInfo.isBondRestricted && ['S'].indexOf(orderForm.action)>=0)
                || (qty && !hasQty)
            )
    }


    const checkPriceChanged = (currPrice, newInfo) => {
        currPrice = numberFrom(currPrice)
        const newPrice = numberFrom(newInfo.invPrice)
        if (!currPrice || !newPrice
            || currPrice === newPrice
            || newInfo.primary==='Y'
            || newInfo.executionType === 'Limit'
        ) {
            return
        }

        modals.setModals({
            view: 'order-ticket-price-change-notice-modal',
            ...newInfo,
            initialPrice: currPrice,
            currentPrice: newPrice,
            callback: () => {
                reloadTicket()
            }
        })
        orderInfo.setOrderInfo(newInfo)
        setIsChangeToRfq(false)
        setTimeout(() => modals.showModals())
        return true
    }

    const previewTicket = async () => {
        setAutoPreview(false)

        const currInfo = (orderInfo.orderInfo || {})
        const currPrice = currInfo.invPrice || currInfo.price
        const currQty = currInfo.qty
        if (!isTreasuryAuction
            && isChangeToRfq
            && orderForm.qty == currQty
            && changeToRfq()
        ) {
            return
        }

        if (!validateTicket()) {
            return
        }

        const isBuySellOrder = ['B','S'].indexOf(orderForm.action)>=0
            && orderForm.mode!==MODES.ORDER_Z
            && !isTreasuryAuction
        if (isBuySellOrder && orderForm.qty != currQty) {
            const newInfo = await reset({
                ...orderForm,
                ...orderInfo,
                ...validation,
                sourceAction_,
                actionSubset_,
                sourceOrderNo_,
                isTreasuryAuction,
                isPreview: true
            })
            if (checkPriceChanged(currPrice, newInfo)) {
                return
            }
        }

        setIsPreviewed(true)

        setTicketStatus(Status.LOADING)
        const info = await preview({
            step,
            ...orderForm,
            ...orderInfo,
            ...attestations,
            sourceAction_,
            actionSubset_,
            sourceOrderNo_,
            isTreasuryAuction
        })
        setTicketStatus(Status.LOADED)

        if (info && isBuySellOrder) {
            if (checkPriceChanged(currPrice, info)) {
                return
            }
        }

        setTimeout(() => { modals.showModals() })

        if (info && !isTreasuryAuction) {
            const isChg =  getIsChangeToRfq({
                orderInfo: info,
                orderForm,
                bondInfo
            })
            setIsChangeToRfq(isChg)
            if (isChg) { return }
        }

        const statusMsg = (info.statusMsg || '').toLowerCase()
        let isFail = !info
            || info.status==='Fail'
            || statusMsg.indexOf('click preview to continue')>0
            || statusMsg.indexOf('10329')>0

        if (attestations.hasAttestCD && statusMsg.indexOf('12697')>0) {
            showModal({
                view: "order-ticket-attestations-modal",
                content: AttestationsCD,
                src: "https://us.etrade.com/l/f/agreement-library/brokered-cd-disclosure",
                title: "E*TRADE from Morgan Stanley Brokered Certificate of Deposit Disclosure Statement",
                label: "I have read the Brokerage CD Disclosure and agree to its terms.",
                value: attestations.attestCD,
                onAttest: attestations.setAttestCD,
            })
        }
        else if (statusMsg.indexOf('29353')>0) {
            const prospectus = bondInfo && bondInfo.bondInfo && bondInfo.bondInfo.issue && bondInfo.bondInfo.issue.prospectus
            if (attestations.hasAttestCorp && prospectus) {
                showModal({
                    view:'order-ticket-attestations-modal',
                    src: prospectus,
                    title: "Prospectus / Offering Document",
                    label: "I have reviewed the prospectus / offering document before placing this order.",
                    value: attestations.attestCorp,
                    onAttest: attestations.setAttestCorp,
                })
            }
        }

        if (isFail) {
            return
        }

        setStep(STEPS.PREVIEW)
    }

    const calculateTicket = async ({isSkipErrors}) => {
        if (!validateTicket({isSkipErrors})) {
            return
        }

        return await preview({
            step, ...orderForm, ...orderInfo, ...attestations,
            isCalc: true,
            sourceAction_, actionSubset_, sourceOrderNo_
        })
    }

    const confirmTicket = async () => {
        const currInfo = (orderInfo.orderInfo || {})
        const currPrice = currInfo.invPrice

        setTicketStatus(Status.LOADING)
        const info = await confirm({
            step,
            ...orderForm,
            ...orderInfo,
            ...attestations,
            sourceAction_,
            actionSubset_,
            sourceOrderNo_,
            isTreasuryAuction
        })
        setTicketStatus(Status.LOADED)

        if (!info || (info.status!=='Ok' && info.status!=='Alert')) {

            if (['S','B'].indexOf(orderForm.action)>=0
                && orderForm.mode!==MODES.ORDER_Z
                && checkPriceChanged(currPrice, info)
            ) {
                setStep(STEPS.ENTRY)
            }

            return
        }

        setStep(STEPS.CONFIRM)
    }

    React.useEffect(() => {
        if (resetingTicket) return

        const {orderInfo:orderInfo_, } = orderInfo
        const {cusip:orderCusip, action:orderAction } = orderInfo_ || {}
        const {cusip, action, } = orderForm

        const isReload = cusip && action
            && (cusip!==orderCusip || action !== orderAction)
        if (!isReload  && !isChangeToRfq) return

        reloadTicket(() => {
            if (['S','B'].indexOf(action)>=0) {
                publish('order_ticket_recalc_totals')
            }
        })

    }, [orderForm.cusip, orderForm.action])

    const reloadTicket = (cb) => {
        setIsChangeToRfq(false)
        setIsRestricted(false)
        setIsPreviewed(false)

        setTicketStatus(Status.LOADING)
        reset({
            ...orderForm,
            ...orderInfo,
            ...validation,
            isPreview: false,
            isTreasuryAuction,
            sourceAction_, actionSubset_, sourceOrderNo_,
            callback: (info) => {
                orderInfo.orderInfo = {...(orderInfo.orderInfo), ...info}
                orderInfo.hiddenState = {...(orderInfo.hiddenState), ...(info && info.hiddenState)}

                setTicketStatus(Status.LOADED)

                const action = orderForm.action
                if (!isTreasuryAuction
                    && orderForm.mode!==MODES.ORDER_Z
                    && !info.price
                    && ['S','B'].indexOf(action)>=0
                ) {
                    orderForm.setAction(action === 'S' ? 'W' : 'F')
                    orderForm.setNoMarketRfq(true)
                    return
                }

                if (cb) { cb() }

                if (!autoPreview) return

                setAutoPreview(false)
                previewTicket()
            }
        })
    }

    React.useEffect(() => {
        if (isChangeToRfq || orderForm.mode===MODES.ORDER_Z) { return }

        const { cusip, action, setQty } = orderForm
        const { positionsTotalQty } = positionsInfo

        if (cusip && (action==='S' || action==='W') && positionsTotalQty>0) {
            setQty(positionsTotalQty)
        }

    }, [orderForm.cusip, orderForm.action, positionsInfo.positionsTotalQty])

    React.useEffect(() => {
        if (resetingTicket) return

        const { orderInfo:info } = orderInfo
        if (!info) return

        const { priceOrYield, setPrice, setYld, mode } = orderForm
        if (mode === MODES.ORDER_Z) return

        if (priceOrYield===1) {
            setPrice(isEmpty(info.price) ? '' : (numberFrom(price_) || info.price))
            setYld('')
        }
        if (priceOrYield===0) {
            setPrice('')
            setYld(isEmpty(info.yield) ? '' : info.yield)
        }

    }, [orderInfo.orderInfo, orderForm.priceOrYield])

    React.useEffect(() => {
        positionsInfo.updatePositions({acctNo:orderForm.acctNo})
    }, [orderForm.acctNo])

    return (
    <Context.Provider value={{
        Last,
        ...orderForm,
        ...positionsInfo,
        ...openOrdersInfo,
        ...accountInfo,
        ...bondInfo,
        ...orderInfo,
        ...attestations,
        ...validation,
        isTreasuryAuction,
        setIsTreasuryAuction,
        isChangeToRfq,
        isRestricted,
        isPreviewed,
        autoPreview,
        ticketStatus,
        previewTicket,
        calculateTicket,
        confirmTicket,
        reloadTicket,
        snapshotView, setSnapshotView,
        step, setStep,
        ts,
        sourceAction_,
        actionSubset_,
        executionType_,
        action_,
        settlement_,
        from,
    }}>
    {children}
    </Context.Provider>
    )
}


export const useOrderTicket = () => React.useContext(Context)

export const getAccounts = () => arrayFrom(global.app.config.accounts)
    .filter(Boolean)

export const getAccount = (acctNo) => arrayFrom(global.app.config.accounts)
    .filter(a => a.acctNo === acctNo)[0]

export const getDefaultAcctNo = () => getAccounts()[0].acctNo || ''


export const allAccounts = () => getAccounts().map(a => a.acctNo).sort()


export const getFirstCusip = (acctNo_) => {
    acctNo_ = acctNo_ || getDefaultAcctNo()
    const h = global.app.config.holdings
        .filter(({acctNo}) => acctNo_ === acctNo)
        .filter(({maturity}) => {
            const isMatured = parseDate(maturity) < Date.now()
            return !isMatured
        })
        .sort(sortFn(({issue}) => (issue || '').trim() ))[0] || {}
    return h.cusip || ''
}